import { MppFileTable } from '@/components/features/Mpp/MppFileTable';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Button } from '@/components/ui/shadcn/button';
import { EmptyState } from '@/components/ui/shadcn/empty-state';
import { Input } from '@/components/ui/shadcn/input';
import { useDebounce } from '@/hooks/use-debounce';
import { useServerFilters } from '@/hooks/use-server-filters';
import { useTranslations } from '@/hooks/useTranslations';
import IndexLayout from '@/layouts/index-layout';
import { cn } from '@/lib/utils';
import { type BreadcrumbItem, type PaginationMeta, type SharedData } from '@/types';
import type { MppFileSummary, MppFilterOption, MppIndexFilters } from '@/types/mpp';
import { Head, router, usePage } from '@inertiajs/react';
import { GanttChartSquare, Loader2, Search, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { route } from 'ziggy-js';

interface MppIndexProps {
    files: MppFileSummary[];
    pagination: PaginationMeta;
    filters: MppIndexFilters;
    statuses: MppFilterOption[];
    users: MppFilterOption[];
}

const ACCEPT = '.mpp,.mpx,.xml,.xer';

export default function MppIndex({ files, pagination, filters, statuses, users }: MppIndexProps) {
    const { t } = useTranslations();
    const { auth } = usePage<SharedData>().props;
    const abilities = auth?.abilities ?? [];
    const canCreate = abilities.includes('mpp.create');
    const canDelete = abilities.includes('mpp.delete');

    const { applyFilters, loading } = useServerFilters(filters as unknown as Record<string, unknown>, '/mpp');

    const inputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);
    const [dragging, setDragging] = useState(false);

    const [searchValue, setSearchValue] = useState(filters.search ?? '');
    const debouncedSearch = useDebounce(searchValue, 800);

    useEffect(() => {
        if (debouncedSearch !== (filters.search ?? '')) {
            applyFilters({ search: debouncedSearch || undefined, page: 1 });
        }
    }, [debouncedSearch, filters.search, applyFilters]);

    const columnFilters: Record<string, string[]> = {
        status: filters.status ?? [],
        uploaded_by: filters.uploaded_by ?? [],
    };
    const hasActiveFilters = (searchValue ?? '') !== '' || columnFilters.status.length > 0 || columnFilters.uploaded_by.length > 0;

    const breadcrumbs: BreadcrumbItem[] = [{ title: t('mpp.index.title', 'MS Project viewer'), href: '/mpp' }];

    const handleFilterChange = (column: string, values: string[]): void => {
        applyFilters({ [column]: values.length > 0 ? values : undefined, page: 1 });
    };

    const handleSortChange = (column: string, direction: 'asc' | 'desc'): void => {
        applyFilters({ sort_by: column, sort_order: direction, page: 1 });
    };

    const clearFilters = (): void => {
        setSearchValue('');
        applyFilters({ search: undefined, status: undefined, uploaded_by: undefined, page: 1 });
    };

    const upload = (fileList: FileList | null): void => {
        const file = fileList?.[0];
        if (!file || uploading) {
            return;
        }

        router.post(
            route('mpp.store'),
            { file },
            {
                forceFormData: true,
                onStart: () => setUploading(true),
                onProgress: (event) => setUploadProgress(event?.percentage ?? null),
                onFinish: () => {
                    setUploading(false);
                    setUploadProgress(null);
                    if (inputRef.current) {
                        inputRef.current.value = '';
                    }
                },
            },
        );
    };

    const onDrop = (event: DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        setDragging(false);
        if (canCreate) {
            upload(event.dataTransfer.files);
        }
    };

    const refresh = (file: MppFileSummary): void => {
        router.post(route('mpp.refresh', file.id), {}, { preserveScroll: true });
    };

    const destroy = (file: MppFileSummary): void => {
        if (window.confirm(t('mpp.index.delete_confirm', 'Delete this file? This cannot be undone.'))) {
            router.delete(route('mpp.destroy', file.id), { preserveScroll: true });
        }
    };

    const uploadButton = canCreate ? (
        <Button onClick={() => inputRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 className="animate-spin" /> : <Upload />}
            {uploading
                ? `${t('mpp.index.uploading', 'Uploading…')}${uploadProgress !== null ? ` ${uploadProgress}%` : ''}`
                : t('mpp.index.upload', 'Upload file')}
        </Button>
    ) : undefined;

    const showEmptyState = pagination.total === 0 && !hasActiveFilters;

    return (
        <>
            <Head title={t('mpp.index.title', 'MS Project viewer')} />
            <IndexLayout
                breadcrumbs={breadcrumbs}
                title={t('mpp.index.title', 'MS Project viewer')}
                subtitle={t('mpp.index.subtitle', 'View Microsoft Project and Primavera schedules without a Project licence.')}
                actions={uploadButton}
            >
                <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={(event) => upload(event.target.files)} />

                {canCreate ? (
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() => inputRef.current?.click()}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                inputRef.current?.click();
                            }
                        }}
                        onDragOver={(event) => {
                            event.preventDefault();
                            setDragging(true);
                        }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={onDrop}
                        className={cn(
                            'bg-card flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-6 py-8 text-center transition-colors',
                            dragging ? 'border-primary bg-accent' : 'hover:border-primary/60',
                        )}
                    >
                        <div className="bg-accent text-accent-foreground flex size-10 items-center justify-center rounded-lg [&>svg]:size-5">
                            <Upload />
                        </div>
                        <p className="text-sm font-medium">{t('mpp.index.dropzone.title', 'Drop a project file here or click to browse')}</p>
                        <p className="text-muted-foreground text-xs">
                            {t('mpp.index.dropzone.hint', 'Supports .mpp, .mpx, .xml (MS Project) and .xer (Primavera) up to 50 MB')}
                        </p>
                    </div>
                ) : null}

                {showEmptyState ? (
                    <EmptyState
                        icon={<GanttChartSquare />}
                        title={t('mpp.index.empty.title', 'No files yet')}
                        description={t(
                            'mpp.index.empty.description',
                            'Upload a Microsoft Project (.mpp) or Primavera file to view it as a Gantt chart.',
                        )}
                    />
                ) : (
                    <>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="relative w-full sm:max-w-xs">
                                <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                                <Input
                                    value={searchValue}
                                    onChange={(event) => setSearchValue(event.target.value)}
                                    placeholder={t('mpp.index.toolbar.search_placeholder', 'Search by file name…')}
                                    className="pl-9"
                                />
                            </div>
                            <div className="text-muted-foreground flex items-center gap-3 text-sm">
                                <span>{t('mpp.index.toolbar.results', ':count files').replace(':count', String(pagination.total))}</span>
                                {hasActiveFilters ? (
                                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                                        <X />
                                        {t('mpp.index.toolbar.clear_filters', 'Clear filters')}
                                    </Button>
                                ) : null}
                            </div>
                        </div>

                        <MppFileTable
                            files={files}
                            statuses={statuses}
                            users={users}
                            sortColumn={filters.sort_by}
                            sortDirection={filters.sort_order}
                            onSortChange={handleSortChange}
                            columnFilters={columnFilters}
                            onFilterChange={handleFilterChange}
                            canDelete={canDelete}
                            onRefresh={refresh}
                            onDelete={destroy}
                            emptyMessage={t('mpp.index.empty_filtered.title', 'No files match')}
                        />

                        <PaginationControls
                            pagination={pagination}
                            onPageChange={(page) => applyFilters({ page })}
                            onPerPageChange={(perPage) => applyFilters({ per_page: perPage, page: 1 })}
                            loading={loading}
                        />
                    </>
                )}
            </IndexLayout>
        </>
    );
}
